import { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { FeatureMode } from '@/modules/common/components/templates/feature-details/types/types';

const TAB_QUERY_PARAM = 'tab';

export interface UseFeatureTabNavigationParams<T extends string> {
  /**
   * Current feature mode (Create/Edit/Read)
   * Create mode uses useState, Edit/Read modes use URL sync
   */
  mode: FeatureMode;

  /**
   * Default tab to show when no tab is specified in URL
   * or when invalid tab is provided
   */
  defaultTab: T;

  /**
   * Array of valid tab values for validation
   * Use Object.values(YourTabEnum) to auto-include all tabs
   */
  validTabs: readonly T[];

  /**
   * Entity ID from route params (e.g., projectId, clientId)
   * Used to detect Create mode when entityId === 'new'
   */
  entityId?: string | number;
}

export interface UseFeatureTabNavigationReturn<T extends string> {
  /**
   * Current active tab
   * - Create mode: From useState
   * - Edit/Read mode: From URL query param (validated)
   */
  currentTab: T;

  /**
   * Function to change the current tab
   * - Create mode: Updates useState
   * - Edit/Read mode: Updates URL query param (preserves other params)
   */
  setCurrentTab: (tab: T) => void;

  /**
   * Advanced navigation with additional query params
   * Useful for buttons that need to switch tabs AND set filters
   * Example: "Show billing changes" button
   */
  navigateToTab: (tab: T, additionalParams?: Record<string, string>) => void;
}

/**
 * Hybrid tab navigation hook for feature detail pages.
 *
 * Automatically handles tab state based on mode:
 * - **Create Mode** (`/projects/new`): Uses useState for clean URLs
 * - **Edit/Read Mode** (`/projects/64`): Syncs with URL query params for deep linking
 *
 * Features:
 * - Tab validation against allowed values
 * - Graceful fallback for invalid tabs
 * - Query param preservation during navigation
 * - Browser history integration (Edit/Read mode)
 *
 * @example
 * ```typescript
 * const { currentTab, setCurrentTab } = useFeatureTabNavigation({
 *   mode: FeatureMode.Edit,
 *   defaultTab: ProjectTab.Info,
 *   validTabs: Object.values(ProjectTab),
 *   entityId: projectId,
 * });
 * ```
 */
export function useFeatureTabNavigation<T extends string>({
  mode,
  defaultTab,
  validTabs,
  entityId,
}: UseFeatureTabNavigationParams<T>): UseFeatureTabNavigationReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stateTab, setStateTab] = useState<T>(defaultTab);

  // Detect Create mode
  const isCreateMode = mode === FeatureMode.Create || entityId === 'new';

  // Parse and validate tab from URL (only in Edit/Read mode)
  const urlTab = useMemo(() => {
    if (isCreateMode) return null;

    const tabParam = searchParams.get(TAB_QUERY_PARAM);
    if (!tabParam) return null;

    // Validate tab name against allowed values
    const isValid = validTabs.includes(tabParam as T);
    return isValid ? (tabParam as T) : null;
  }, [searchParams, validTabs, isCreateMode]);

  // Determine current tab based on mode
  const currentTab = isCreateMode ? stateTab : (urlTab ?? defaultTab);

  // Tab change handler
  const setCurrentTab = useCallback(
    (newTab: T) => {
      if (isCreateMode) {
        // Create mode: just update state
        setStateTab(newTab);
      } else {
        // Edit/Read mode: update URL preserving other params
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set(TAB_QUERY_PARAM, newTab);
            return newParams;
          },
          { replace: false }
        ); // Don't replace - allows browser back/forward
      }
    },
    [isCreateMode, setSearchParams]
  );

  // Navigation helper with additional params
  const navigateToTab = useCallback(
    (tab: T, additionalParams?: Record<string, string>) => {
      if (isCreateMode) {
        setStateTab(tab);
        return;
      }

      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set(TAB_QUERY_PARAM, tab);

          // Add additional params if provided
          if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
              newParams.set(key, value);
            });
          }

          return newParams;
        },
        { replace: false }
      );
    },
    [isCreateMode, setSearchParams]
  );

  return {
    currentTab,
    setCurrentTab,
    navigateToTab,
  };
}
